import * as React from 'react';
import cn from 'classnames';
import { Icon } from '../Icon/Icon';
import { useCheckbox } from './useCheckbox';
import './Checkbox.css';
import { State } from '../../types';

export type CheckboxProps = React.InputHTMLAttributes<HTMLInputElement> & {
    /**	Set whether the checkbox is checked or unchecked. */
    checked?: boolean;
    /** Draws attention to the checkbox by changing its style and making it visually prominent */
    highlighted?: boolean;
    /**
     * Set whether the checkbox is indeterminate.
     * Used only with sub-checkboxes. The indeterminate state is shown if not all sub-checkboxes are selected.
     * This only affects the style, changing the icon in the checkbox.
     */
    indeterminate?: boolean;
    /** Text displayed next to the checkbox */
    text?: React.ReactNode;
    /** State will change the style of the checkbox **/
    state?: State;
};

export const Checkbox = React.forwardRef(function Checkbox(props: CheckboxProps, ref: React.Ref<HTMLInputElement>) {
    const { className: externalClassName, highlighted, text, state, style, ...otherProps } = props;
    const {
        input: { ref: internalRef, ...input },
    } = useCheckbox(otherProps, ref);

    const className = cn('yt-input', 'yt-checkmark', {
        'yt-input--disabled': props.disabled,
        [`yt-input--state-${state}`]: !!state,
        'yt-input--highlighted': highlighted,
    });
    const isIndeterminate = input['aria-checked'] === 'mixed';

    React.useEffect(() => {
        if (internalRef && internalRef.current) {
            internalRef.current.indeterminate = isIndeterminate;
        }
    }, [internalRef, isIndeterminate]);

    return (
        <span className={cn('flex items-center font-normal h-8', externalClassName)} style={style}>
            <span className="yt-checkbox">
                <input ref={internalRef} {...input} />
                <span className={className}>{input.checked ? <Icon name="tick" /> : isIndeterminate ? <span /> : null}</span>
            </span>
            {text && <span className="cursor-pointer ml-2 text-sm leading-relaxed self-start pt-1 mt-px">{text}</span>}
        </span>
    );
});
